import { useEffect, useMemo, useRef, useState } from 'react';
import { getSocketServerUrl } from '../services/socketConfig';

function ScannerPage() {
  const scannerRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const lastSentRef = useRef({ value: '', at: 0 });

  const [channel, setChannel] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('channel') || '';
  });
  const [manualBarcode, setManualBarcode] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [scannerStatus, setScannerStatus] = useState('idle');
  const [socketStatus, setSocketStatus] = useState('disconnected');
  const [lastSentBarcode, setLastSentBarcode] = useState('');
  const [autoStart, setAutoStart] = useState(true);

  const socketUrl = useMemo(() => getSocketServerUrl(), []);

  const stopCamera = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScannerStatus('idle');
  };

  const sendBarcode = (rawBarcode) => {
    const barcode = String(rawBarcode || '').trim();
    if (!barcode || !channel || wsRef.current?.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(
      JSON.stringify({
        type: 'barcode_scanned',
        channel,
        barcode,
      })
    );

    setLastSentBarcode(barcode);
  };

  const maybeSendBarcode = (barcode) => {
    const now = Date.now();
    const previous = lastSentRef.current;
    if (previous.value === barcode && now - previous.at < 1500) {
      return;
    }

    lastSentRef.current = { value: barcode, at: now };
    sendBarcode(barcode);
  };

  const startCamera = async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      
      const scanner = new Html5Qrcode('scanner-region');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          maybeSendBarcode(decodedText);
        },
        () => {}
      );

      setScannerStatus('scanning');
    } catch (err) {
      console.error('Scanner error:', err);
      setCameraError('Unable to start camera. Please allow camera permission.');
      stopCamera();
    }
  };

  useEffect(() => {
    if (!channel) {
      setSocketStatus('disconnected');
      return undefined;
    }

    let cancelled = false;

    const connect = () => {
      const ws = new WebSocket(socketUrl);
      wsRef.current = ws;
      setSocketStatus('connecting');

      ws.onopen = () => {
        if (cancelled) return;
        setSocketStatus('connected');
        ws.send(
          JSON.stringify({
            type: 'join',
            role: 'scanner',
            channel,
          })
        );

        if (autoStart && scannerStatus === 'idle') {
          startCamera();
        }
      };

      ws.onclose = () => {
        if (cancelled) return;
        setSocketStatus('disconnected');
        reconnectTimerRef.current = setTimeout(connect, 2000);
      };

      ws.onerror = () => {
        if (cancelled) return;
        setSocketStatus('error');
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [channel, socketUrl, autoStart, scannerStatus]);

  useEffect(() => {
    return () => {
      stopCamera();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, []);

  const handleManualSubmit = (event) => {
    event.preventDefault();
    const barcode = manualBarcode.trim();
    if (!barcode) return;
    maybeSendBarcode(barcode);
    setManualBarcode('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-maroon-50 to-maroon-100 p-4 md:p-8">
      <div className="mx-auto w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl shadow-maroon-900/10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-maroon-900">Mobile Barcode Scanner</h1>
            <p className="mt-1 text-sm text-maroon-700">
              Scan barcode and send to POS cart via socket.
            </p>
          </div>
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2">
              <span className={`h-3 w-3 rounded-full ${
                socketStatus === 'connected' ? 'bg-green-500' : 
                socketStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' : 
                'bg-gray-400'
              }`} />
              <span className="text-xs text-maroon-600">{socketStatus}</span>
            </div>
            {lastSentBarcode && (
              <p className="text-xs text-green-600">Sent: {lastSentBarcode}</p>
            )}
          </div>
        </div>

        <label className="mt-4 block text-sm font-medium text-maroon-900" htmlFor="scanner_channel">
          POS Channel
        </label>
        <input
          id="scanner_channel"
          type="text"
          value={channel}
          onChange={(event) => setChannel(event.target.value.trim())}
          className="mt-1 w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
          placeholder="Enter POS channel"
        />

        <label className="mt-3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={autoStart}
            onChange={(e) => setAutoStart(e.target.checked)}
            className="h-4 w-4 rounded border-maroon-300"
          />
          <span className="text-sm text-maroon-700">Auto-start camera when connected</span>
        </label>

        <div className="mt-4 overflow-hidden rounded-xl border border-maroon-200 bg-black">
          <div id="scanner-region" className="h-64 w-full overflow-hidden" />
        </div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={startCamera}
            disabled={scannerStatus === 'scanning'}
            className="flex-1 rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white hover:bg-maroon-800 disabled:opacity-60"
          >
            {scannerStatus === 'scanning' ? 'Scanning...' : 'Start Camera'}
          </button>
          <button
            type="button"
            onClick={stopCamera}
            disabled={scannerStatus !== 'scanning'}
            className="flex-1 rounded-lg border border-maroon-300 px-4 py-2 text-sm font-semibold text-maroon-800 hover:bg-maroon-50 disabled:opacity-60"
          >
            Stop
          </button>
        </div>

        {cameraError ? <p className="mt-2 text-sm text-red-600">{cameraError}</p> : null}

        <form onSubmit={handleManualSubmit} className="mt-4">
          <label className="block text-sm font-medium text-maroon-900" htmlFor="manual_barcode">
            Manual Barcode / QR Code
          </label>
          <div className="mt-1 flex gap-2">
            <input
              id="manual_barcode"
              type="text"
              value={manualBarcode}
              onChange={(event) => setManualBarcode(event.target.value)}
              placeholder="Type barcode or paste value"
              className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
            />
            <button
              type="submit"
              disabled={!manualBarcode.trim()}
              className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white hover:bg-maroon-800 disabled:opacity-60"
            >
              Send
            </button>
          </div>
        </form>

        <div className="mt-4 rounded-lg border border-maroon-100 bg-maroon-50 p-3">
          <h3 className="text-sm font-semibold text-maroon-900">How to use:</h3>
          <ol className="mt-2 list-inside list-decimal space-y-1 text-xs text-maroon-700">
            <li>Open POS page and enable "External Scanner"</li>
            <li>Copy the channel code shown in POS</li>
            <li>Paste the channel code above</li>
            <li>Allow camera permission and start scanning</li>
            <li>Items will be added automatically to POS cart</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default ScannerPage;
